import { ActionIcon, Box, Group, Table, Text, Tooltip } from "@mantine/core"
import { EyeIcon, ShoppingCartSimpleIcon, TrashIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { ListState } from "../../ui"
import qoClasses from "../quick-order/quick-order.module.css"
import classes from "./saved-lists.module.css"

export type SourceRow = {
  id: string
  name: string
  sub: string
  date: string
}

type Props = {
  kind: "saved" | "order"
  rows: SourceRow[]
  state: "loading" | "error" | "empty" | "data"
  selectedId: string | null
  note?: string
  onRetry: () => void
  onSelect: (id: string) => void
  onAddToCart: (id: string) => void
  /** The supplier catalogue (warehouses) is still loading. */
  addDisabled?: boolean
  /** Saved lists only; past orders cannot be deleted from here. */
  onDelete?: (id: string) => void
  onEmptyAction: () => void
}

/** Left table of OrdB_SavedLists: «Үйлдэл» first (view, to cart, delete), then name and date. */
export function SourceList({ kind, rows, state, selectedId, note, onRetry, onSelect, onAddToCart, addDisabled = false, onDelete, onEmptyAction }: Readonly<Props>) {
  const { t } = useTranslation("bulkOrder")

  return (
    <Box className={qoClasses.card}>
      {state === "loading" && <ListState state="loading" columns={3} rows={6} />}
      {state === "error" && <ListState state="error" title={t("saved.errorTitle")} onRetry={onRetry} />}
      {state === "empty" && (
        <ListState
          state="empty"
          title={t(kind === "saved" ? "saved.emptyTitle" : "saved.ordersEmptyTitle")}
          description={t(kind === "saved" ? "saved.emptyText" : "saved.ordersEmptyText")}
          action={{ label: t("saved.toCatalog"), onClick: onEmptyAction }}
        />
      )}
      {state === "data" && (
        <Table verticalSpacing="sm" highlightOnHover layout="fixed">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={136}>{t("saved.col.action")}</Table.Th>
              <Table.Th>{t(kind === "saved" ? "saved.col.name" : "saved.col.order")}</Table.Th>
              <Table.Th w={116}>{t("saved.col.date")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr
                key={row.id}
                className={classes.row}
                data-selected={row.id === selectedId || undefined}
                onClick={() => onSelect(row.id)}
              >
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  <Group gap={6} wrap="nowrap">
                    <Tooltip label={t("saved.view")}>
                      <ActionIcon variant="subtle" color="gray" size="lg" aria-label={t("saved.view")} onClick={() => onSelect(row.id)}>
                        <EyeIcon size={18} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={t("saved.addAll")}>
                      <ActionIcon variant="subtle" color="gray" size="lg" aria-label={t("saved.addAll")} disabled={addDisabled} onClick={() => onAddToCart(row.id)}>
                        <ShoppingCartSimpleIcon size={18} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={onDelete ? t("saved.delete") : t("saved.deleteOrderNo")}>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="lg"
                        aria-label={t("saved.delete")}
                        disabled={!onDelete}
                        onClick={() => onDelete?.(row.id)}
                      >
                        <TrashIcon size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text className={kind === "order" ? qoClasses.mono : undefined} fz="var(--nw-fz-body)" fw={500} translate="no" truncate>
                    {row.name}
                  </Text>
                  <Text className={classes.sub} lineClamp={2}>
                    {row.sub}
                  </Text>
                </Table.Td>
                <Table.Td className={qoClasses.mono}>{row.date}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {note && state === "data" && <Text className={classes.listNote}>{note}</Text>}
    </Box>
  )
}
